'use client';

import { useEffect, useMemo, PropsWithChildren, useCallback, useRef, useState } from 'react';

import {
  AssistantRuntimeProvider,
  type AssistantCloud,
  Tools,
  useAui,
  useCloudThreadListAdapter,
  useRemoteThreadListRuntime,
} from '@assistant-ui/react';
import { useChatRuntime } from '@assistant-ui/react-ai-sdk';
import { useLocale } from 'next-intl';
import { useLatest } from 'react-use';

import { credentialToolNames, useHitl } from '@/components/assistant-ui/hitl/hitl-provider';
import { useConfigValue, useTenantName } from '@/components/providers/config-provider';
import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { useDevTools } from '@/components/providers/dev-tools-provider';
import type { Platform } from '@/components/providers/platform-provider';
import { useStorageAccess } from '@/components/providers/storage-access-provider';
import { useCrossTabThreadSync } from '@/hooks/use-cross-tab-thread-sync';
import { useDeletionConfirmation } from '@/hooks/use-deletion-confirmation';
import { useDictationAdapter } from '@/hooks/use-dictation-adapter';
import { useResponseInterruption } from '@/hooks/use-response-interruption';
import { useResponseTracking, ResponseTrackingContext } from '@/hooks/use-response-tracking';
import { useResumableStreamDiscovery } from '@/hooks/use-resumable-stream-discovery';
import { useSafePostHog } from '@/hooks/use-safe-posthog';
import { useServerThreadTitleSync } from '@/hooks/use-server-thread-title-sync';
import { useThreadPersistence } from '@/hooks/use-thread-persistence';
import { useThreadStatusFeed } from '@/hooks/use-thread-status-feed';
import { useTrackEvent } from '@/hooks/use-track-event';
import { useTtsInterruption } from '@/hooks/use-tts-interruption';
import { AssistantCloudLocal } from '@/lib/assistant-cloud-local';
import type { AnalyticsChatTransport } from '@/lib/assistant-cloud-local/analytics-chat-transport';
import {
  createChatTransport,
  type HitlTransportBindings,
} from '@/lib/assistant-cloud-local/create-chat-transport';
import { AssistantCloudServer } from '@/lib/assistant-cloud-server';
import {
  NETWORK_ERROR_TO_ABORT_REASON,
  type AbortReason,
} from '@/lib/constants/post-message-types';
import { createTenantThreadResumableStorage } from '@/lib/hitl/resumable-storage';
import { lastAssistantMessageIsCompleteWithToolCallsOrApprovalResponses } from '@/lib/hitl/send-automatically-when';
import { readBootThreadId } from '@/lib/sync/store';
import { useThreadStatusStore } from '@/lib/thread-status/store';
import { ElevenLabsSpeechAdapter, TTS_PROVIDER } from '@/lib/tts/elevenlabs-speech-adapter';
import type { TtsConfig } from '@/lib/types/tenant';
import { setDeletionQueueMode } from '@/lib/utils/deletion-queue';
import { generateMessageId, popServerMessageId } from '@/lib/utils/message';
import { getNetworkErrorType } from '@/lib/utils/network-errors';
import { resolveServerResourceId } from '@/lib/utils/resource-id';
import { maybeDeriveTicketUrl } from '@/lib/utils/ticket-env';

const DEFAULT_CHAT_API_BASE_URL = process.env.NEXT_PUBLIC_CHAT_API_BASE_URL ?? '';

function ResponseTracker() {
  useResponseTracking();
  useResponseInterruption();
  useTtsInterruption();
  return null;
}

function ThreadManager() {
  useThreadPersistence();
  useDeletionConfirmation();
  return null;
}

function ThreadStatusManager() {
  useThreadStatusFeed();
  useServerThreadTitleSync();
  useResumableStreamDiscovery();
  useCrossTabThreadSync();
  return null;
}

// Registers render-only approval UIs for server-executed approval tools via setToolUI.
// NEVER defineToolkit for these — toolkit entries get advertised to the backend.
function ApprovalUIRegistrar() {
  const aui = useAui();
  const { approvalUIs } = useHitl();

  useEffect(() => {
    if (!approvalUIs) return;
    const unsubscribes = Object.entries(approvalUIs).map(([toolName, render]) =>
      aui.tools.setToolUI(toolName, render),
    );
    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
  }, [aui, approvalUIs]);

  return null;
}

export function CloudLocalRuntimeProvider({
  children,
  platform,
}: PropsWithChildren<{ platform?: Platform }>) {
  const posthog = useSafePostHog();
  const trackEvent = useTrackEvent();

  const { hasStorageAccess } = useStorageAccess();
  const { isDebugMode } = useDebugMode();
  const { showDevTools, selectedModel, provider, reasoning } = useDevTools();
  const devToolsRef = useLatest({ showDevTools, selectedModel, provider, reasoning });
  const currentThreadIdRef = useRef<string | null>(null);
  const erroredThreadIdsRef = useRef<Set<string>>(new Set());
  const pendingResponsesRef = useRef<Map<string, number>>(new Map());
  const abortReasonsRef = useRef<Map<string, AbortReason>>(new Map());
  const streamingStartedRef = useRef<Set<string>>(new Set());
  const retriedResponsesRef = useRef<Set<string>>(new Set());
  const lastMessageCountRef = useRef<Map<string, number>>(new Map());
  const locale = useLocale();
  const localeRef = useLatest(locale);

  const dictationAdapter = useDictationAdapter();

  const trackEventRef = useLatest(trackEvent);

  const ttsConfig = useConfigValue<TtsConfig>('meta.tts');
  const speechAdapter = useMemo(() => {
    if (!ttsConfig?.enabled) return undefined;
    if (ttsConfig.provider !== TTS_PROVIDER) return undefined;
    return new ElevenLabsSpeechAdapter({
      voiceId: ttsConfig.voiceId,
      modelId: ttsConfig.modelId,
      voiceSettings: ttsConfig.voiceSettings,
      volume: ttsConfig.volume,
      localeRef,
      threadIdRef: currentThreadIdRef,
      onAnalyticsEvent: (event, properties) => trackEventRef.current({ event, properties }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsConfig?.enabled, ttsConfig?.provider, ttsConfig?.voiceId, ttsConfig?.modelId]);

  const platformRef = useLatest(platform);
  const isDebugModeRef = useLatest(isDebugMode);
  const posthogRef = useLatest(posthog);

  const tenantName = useTenantName();
  const chatApiBaseUrl =
    typeof window === 'undefined'
      ? DEFAULT_CHAT_API_BASE_URL
      : maybeDeriveTicketUrl(DEFAULT_CHAT_API_BASE_URL, window.location.host);

  // Server-persistence tenants must never construct the local cloud — its constructor opens
  // IndexedDB; never constructing it is what guarantees zero IDB usage.
  const persistence = useConfigValue<'local' | 'server'>('persistence') ?? 'local';
  // Route deletions through the in-memory queue (not IndexedDB) for server-persistence tenants.
  // Effect, not the useMemo body: deletions are user-triggered post-mount, so the effect always
  // wins the race, and a side-effect belongs outside render.
  useEffect(() => {
    if (persistence === 'server') setDeletionQueueMode('memory');
  }, [persistence]);
  const cloud = useMemo(() => {
    if (persistence !== 'server') return new AssistantCloudLocal();
    return new AssistantCloudServer({
      baseUrl: chatApiBaseUrl,
      tenant: tenantName,
      getResourceId: () => resolveServerResourceId(posthogRef.current?.get_distinct_id()),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistence, chatApiBaseUrl, tenantName]);

  const hitl = useHitl();
  const hitlRef = useRef<HitlTransportBindings | null>(null);
  const hitlCredentialTools = credentialToolNames(hitl);
  hitlRef.current = hitl.getCredentials
    ? { getCredentials: hitl.getCredentials, credentialToolNames: hitlCredentialTools }
    : null;

  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;
  const hitlSuppressed = useConfigValue<boolean>('hitlSuppressed') === true;

  // Lazy ref-init: the rule's officially-recommended pattern for one-time
  // construction (the rule's own hint suggests `if (ref.current == null) { ... }`).
  // The transport's option callbacks run at request time and read live values via
  // refs, so the transport never needs to be re-created when inputs change.
  const transportRef = useRef<AnalyticsChatTransport | null>(null);
  if (transportRef.current === null) {
    transportRef.current = createChatTransport({
      chatApiBaseUrl,
      tenantName,
      localeRef,
      platformRef,
      isDebugModeRef,
      posthogRef,
      trackEventRef,
      currentThreadIdRef,
      erroredThreadIdsRef,
      pendingResponsesRef,
      streamingStartedRef,
      retriedResponsesRef,
      lastMessageCountRef,
      devToolsRef,
      hitlRef,
      hitlSuppressed,
      serverPersistence: persistence === 'server',
      // Resume endpoint requires the resourceId query param — binding is verified server-side.
      resumable: resumableEnabled
        ? {
            storage: createTenantThreadResumableStorage({ tenantName, currentThreadIdRef }),
            resumeApi: (streamId: string) =>
              `${chatApiBaseUrl}/${tenantName}/chat/resume/${streamId}?resourceId=${encodeURIComponent(
                resolveServerResourceId(posthogRef.current?.get_distinct_id()),
              )}`,
          }
        : undefined,
    });
  }

  const handleError = useCallback(
    (error: unknown) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[chat-runtime-error]', error);
      }
      const threadId = currentThreadIdRef.current;
      const startTime = threadId ? pendingResponsesRef.current.get(threadId) : undefined;

      const networkErrorType = getNetworkErrorType(error);
      const abortReason = NETWORK_ERROR_TO_ABORT_REASON[networkErrorType];

      posthog?.captureException(error, {
        threadId,
        source: 'chat-runtime',
        isDocumentHidden:
          typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
        isNetworkOffline: !navigator.onLine,
        requestDurationMs: startTime ? Date.now() - startTime : undefined,
        abortReason,
      });

      if (threadId) {
        erroredThreadIdsRef.current.add(threadId);
        abortReasonsRef.current.set(threadId, abortReason);
        // Thread-status dots are a server-persistence-only feature — gate the write like the feed
        // so non-opted tenants show no error icon (byte-identical sidebar).
        if (persistence === 'server') useThreadStatusStore.getState().markError(threadId);
        // Clean up serverMessageId to prevent memory leak
        popServerMessageId(threadId);
      }

      // IMPORTANT: Do NOT re-throw the error here!
      // If we re-throw, the AI SDK's error handler never completes, and the status
      // remains "streaming" instead of transitioning to "error". This leaves the UI stuck.
      // By returning normally, the AI SDK sets status to "error" and isRunning becomes false.
    },
    [posthog, persistence],
  );

  // Read once, synchronously, before the runtime is constructed: passed to
  // useRemoteThreadListRuntime as initialThreadId, whose __internal_load switches to it —
  // adopting unlisted ids via adapter.fetch and swallowing failures, so a stale/deleted id
  // degrades to the default new thread. Replaces the old post-boot restore effect in
  // useThreadPersistence.
  const [initialThreadId] = useState(() => readBootThreadId() ?? undefined);

  // Compose the framework's remote-thread-list runtime ourselves (instead of useChatRuntime's
  // built-in composition) so the cloud thread list and initialThreadId are supplied by the OUTER
  // runtime with vanilla assistant-ui. useChatRuntime (no cloud) becomes the per-thread
  // runtimeHook: its own nested useRemoteThreadListRuntime detects the surrounding thread-list
  // context and, with allowNesting, degrades to returning just the per-thread runtime (which
  // carries the resume mechanism from the react-ai-sdk patch). This keeps initialThreadId
  // forwarding out of the patch.
  const chatRuntime = useRemoteThreadListRuntime({
    runtimeHook: function RuntimeHook() {
      return useChatRuntime({
        transport: transportRef.current!,
        onError: handleError,
        generateId: generateMessageId,
        // After a tool call completes (incl. HITL human tools resolving via addResult) OR an
        // approval response lands (respondToApproval on a server-side gate), send back so the
        // agent produces its reply. The stock tool-calls predicate alone never auto-sends
        // approval responses.
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCallsOrApprovalResponses,
        adapters: {
          dictation: dictationAdapter,
          speech: speechAdapter,
        },
      });
    },
    // AssistantCloudLocal/Server are structural drop-ins for the upstream AssistantCloud but
    // don't nominally extend it; bridge the nominal mismatch without an `any`.
    adapter: useCloudThreadListAdapter({ cloud: cloud as unknown as AssistantCloud }),
    initialThreadId,
  });

  // Register the tenant's HITL client tools (client-side `type: "human"` tools) so their
  // approval cards render and resolve in the browser. Empty toolkit when the tenant has none.
  // Server-side approval tools are NOT in the toolkit (its entries get advertised to the
  // backend as client tools) — their custom UIs register render-only via setToolUI below.
  const aui = useAui({ tools: Tools({ toolkit: hitl.toolkit }) });

  const responseTrackingValue = useMemo(
    () => ({
      pendingResponsesRef,
      abortReasonsRef,
      streamingStartedRef,
      retriedResponsesRef,
    }),
    [],
  );

  return (
    <AssistantRuntimeProvider aui={aui} runtime={chatRuntime}>
      <ResponseTrackingContext.Provider value={responseTrackingValue}>
        <ApprovalUIRegistrar />
        <ResponseTracker />
        <ThreadStatusManager />
        {hasStorageAccess && <ThreadManager />}
        {children}
      </ResponseTrackingContext.Provider>
    </AssistantRuntimeProvider>
  );
}
